import type paper from "paper";
import type {
  ConstructionLine,
  ConstructionOutline,
  OutlineEndpoint,
  OutlineSegmentRef,
  Vec2,
} from "./Project";
import {
  faceAndNeighbors,
  faceContainingPoint,
  type FaceEdge,
} from "./planarFaces";

/** Region click-to-fill (#651): node a set of construction lines into a planar
 *  arrangement (split each line at its vertices and at every intersection with
 *  another line), so `planarFaces` can find the region under a click and we can
 *  map that face's boundary back to construction-segment references. */

const NODE_TOL = 1e-3;
/** Max distance a recorded intersection may sit from a subpath to count as
 *  lying on it (intersections are exact, this only guards float noise). */
const ON_PATH_TOL = 0.01;

interface ArrNode extends Vec2 {
  /** Stable endpoint ref for this node (anchor vertex or line×line crossing). */
  ref: OutlineEndpoint;
}

interface ArrEdge {
  /** Canonical direction a→b is increasing arc-offset along the subpath (and
   *  the closing wrap for a closed subpath), with no split point between. */
  a: number;
  b: number;
  sourceId: string;
  pathIndex: number;
  closed: boolean;
}

export interface ConstructionArrangement {
  nodes: ArrNode[];
  edges: ArrEdge[];
}

function subpathsOf(cp: paper.CompoundPath): paper.Path[] {
  return cp.children && cp.children.length > 0
    ? (cp.children as paper.Path[])
    : [cp as unknown as paper.Path];
}

/** Build the planar arrangement of the given construction lines. */
export function buildConstructionArrangement(
  lines: ConstructionLine[],
  scope: paper.PaperScope,
): ConstructionArrangement {
  const parsed: Array<{ id: string; cp: paper.CompoundPath; subs: paper.Path[] }> =
    [];
  for (const l of lines) {
    if (l.visible === false) continue;
    try {
      const cp = new scope.CompoundPath({ pathData: l.pathData, insert: false });
      parsed.push({ id: l.id, cp, subs: subpathsOf(cp) });
    } catch {
      /* skip malformed */
    }
  }

  // Intersection points recorded on each participating line.
  const intersByLine = new Map<string, Array<Vec2 & { partnerId: string }>>();
  const pushInter = (id: string, x: number, y: number, partnerId: string) => {
    const arr = intersByLine.get(id) ?? [];
    arr.push({ x, y, partnerId });
    intersByLine.set(id, arr);
  };
  for (let i = 0; i < parsed.length; i++) {
    for (let j = i + 1; j < parsed.length; j++) {
      let inters: paper.CurveLocation[];
      try {
        inters = parsed[i]!.cp.getIntersections(parsed[j]!.cp);
      } catch {
        continue;
      }
      for (const it of inters) {
        pushInter(parsed[i]!.id, it.point.x, it.point.y, parsed[j]!.id);
        pushInter(parsed[j]!.id, it.point.x, it.point.y, parsed[i]!.id);
      }
    }
  }

  const nodes: ArrNode[] = [];
  const edges: ArrEdge[] = [];
  const findOrAddNode = (x: number, y: number, ref: OutlineEndpoint): number => {
    for (let k = 0; k < nodes.length; k++) {
      if (Math.hypot(nodes[k]!.x - x, nodes[k]!.y - y) < NODE_TOL) {
        // Prefer a vertex (anchor) ref over an intersection at a T-junction.
        if (ref.kind === "anchor" && nodes[k]!.ref.kind !== "anchor") {
          nodes[k]!.ref = ref;
        }
        return k;
      }
    }
    nodes.push({ x, y, ref });
    return nodes.length - 1;
  };

  try {
    for (const p of parsed) {
      const lineInters = intersByLine.get(p.id) ?? [];
      for (let si = 0; si < p.subs.length; si++) {
        const sub = p.subs[si]!;
        if (sub.length <= NODE_TOL) continue;
        const splits: Array<{ offset: number; x: number; y: number; ref: OutlineEndpoint }> =
          [];
        // Vertices.
        sub.segments.forEach((seg, segi) => {
          const off = sub.getNearestLocation(seg.point)?.offset ?? 0;
          splits.push({
            offset: off,
            x: seg.point.x,
            y: seg.point.y,
            ref: { kind: "anchor", constructionId: p.id, pathIndex: si, segmentIndex: segi },
          });
        });
        // Intersections that lie on this subpath.
        for (const it of lineInters) {
          const loc = sub.getNearestLocation(new scope.Point(it.x, it.y));
          if (!loc || loc.point.getDistance(new scope.Point(it.x, it.y)) > ON_PATH_TOL) {
            continue;
          }
          splits.push({
            offset: loc.offset,
            x: it.x,
            y: it.y,
            ref: { kind: "intersection", aId: p.id, bId: it.partnerId, hint: { x: it.x, y: it.y } },
          });
        }
        if (splits.length < 2) continue;
        splits.sort((m, n) => m.offset - n.offset);
        // Merge split points at the same offset (a vertex coincident with an
        // intersection); keep the anchor ref.
        const merged: typeof splits = [];
        for (const s of splits) {
          const last = merged[merged.length - 1];
          if (last && Math.abs(last.offset - s.offset) < NODE_TOL) {
            if (s.ref.kind === "anchor") last.ref = s.ref;
            continue;
          }
          merged.push({ ...s });
        }
        const idxs = merged.map((s) => findOrAddNode(s.x, s.y, s.ref));
        const closed = !!sub.closed;
        for (let k = 0; k < idxs.length - 1; k++) {
          if (idxs[k] !== idxs[k + 1]) {
            edges.push({ a: idxs[k]!, b: idxs[k + 1]!, sourceId: p.id, pathIndex: si, closed });
          }
        }
        if (closed && idxs.length >= 2 && idxs[idxs.length - 1] !== idxs[0]) {
          edges.push({ a: idxs[idxs.length - 1]!, b: idxs[0]!, sourceId: p.id, pathIndex: si, closed });
        }
      }
    }
  } finally {
    for (const p of parsed) p.cp.remove();
  }

  return { nodes, edges };
}

/** Map a found face (ordered CCW half-edges) to a closed construction outline. */
export function faceToOutline(
  face: FaceEdge[],
  arr: ConstructionArrangement,
): ConstructionOutline {
  const segments: OutlineSegmentRef[] = [];
  for (const fe of face) {
    const edge = arr.edges[fe.edge]!;
    // Canonical edge direction a→b is increasing offset; if this half-edge runs
    // the other way on a closed subpath, take the complementary (reverse) arc.
    const reversed = edge.closed && fe.from === edge.b;
    segments.push({
      sourceId: edge.sourceId,
      pathIndex: edge.pathIndex,
      start: arr.nodes[fe.from]!.ref,
      end: arr.nodes[fe.to]!.ref,
      reversed,
    });
  }
  return { segments, closed: true };
}

/** The bounded face under `point` (its CCW half-edge loop), or null. */
export function faceAt(
  arr: ConstructionArrangement,
  point: Vec2,
): FaceEdge[] | null {
  return faceContainingPoint(arr.nodes, arr.edges, point);
}

/** The face under `point` plus whether it has an adjacent region to merge with
 *  (used to commit immediately when a clicked shape is isolated). */
export function faceAndNeighborsAt(
  arr: ConstructionArrangement,
  point: Vec2,
): { face: FaceEdge[]; hasNeighbor: boolean } | null {
  return faceAndNeighbors(arr.nodes, arr.edges, point);
}

/** The closed outline for the region under `point`, or null if `point` is not
 *  enclosed by the construction lines. */
export function regionOutlineAt(
  arr: ConstructionArrangement,
  point: Vec2,
): ConstructionOutline | null {
  const face = faceAt(arr, point);
  return face ? faceToOutline(face, arr) : null;
}

/** Stable identity for a face (its set of edges) so the region tool can
 *  toggle faces in/out of a multi-select. */
export function faceKey(face: FaceEdge[]): string {
  return face
    .map((fe) => fe.edge)
    .sort((a, b) => a - b)
    .join(",");
}

/** Merge a set of (adjacent) faces into one closed outline: keep the boundary
 *  half-edges — those whose twin isn't also in the set (shared interior edges
 *  drop out) — and chain them into a loop. Returns null if they don't form a
 *  single closed boundary. */
export function mergeFacesOutline(
  faces: FaceEdge[][],
  arr: ConstructionArrangement,
): ConstructionOutline | null {
  if (faces.length === 0) return null;
  if (faces.length === 1) return faceToOutline(faces[0]!, arr);

  const present = new Set<string>();
  for (const f of faces) {
    for (const fe of f) present.add(`${fe.edge}:${fe.from}:${fe.to}`);
  }
  const boundary: FaceEdge[] = [];
  for (const f of faces) {
    for (const fe of f) {
      if (!present.has(`${fe.edge}:${fe.to}:${fe.from}`)) boundary.push(fe);
    }
  }
  if (boundary.length < 3) return null;

  // Chain boundary half-edges head-to-tail into a single loop.
  const byFrom = new Map<number, FaceEdge[]>();
  for (const fe of boundary) {
    const arr2 = byFrom.get(fe.from) ?? [];
    arr2.push(fe);
    byFrom.set(fe.from, arr2);
  }
  const used = new Set<FaceEdge>();
  const loop: FaceEdge[] = [];
  let cur: FaceEdge | undefined = boundary[0];
  for (let guard = 0; guard <= boundary.length && cur; guard++) {
    if (used.has(cur)) break;
    used.add(cur);
    loop.push(cur);
    cur = (byFrom.get(cur.to) ?? []).find((c) => !used.has(c));
  }
  if (loop.length < 3) return null;
  return faceToOutline(loop, arr);
}
